import {useMemo, useCallback} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {convertFiltersToSegment, applySegmentFilters} from '@/utils/segments';
import {
  type Segment,
  type SegmentFilters,
  isCampaignSegment,
} from '@/types/models/segments/segment';
import useBrandCampaignsSegments from '@/views/creators/creator-crm/hooks/use-brand-campaigns-segments';
import searchCreatorsGetters from '@/views/creators/creator-crm/utils/searchCreatorsGetters';
import useSegmentsQuery from '@/hooks/queries/use-segments-query/useSegmentsQuery';
import useCreatorCrmStore from '@/stores/creator-crm/creatorCrmStore';

export type UseSegmentsOptions = {
  merchantId: string;
  brandId?: string;
  enabled?: boolean;
  selectedSegmentId?: string | null;
};

export type UseSegmentsResult = {
  segments: Segment[];
  currentFiltersForSegment: SegmentFilters;
  activeSegment: Segment | null;
  activeCampaignSegment: Segment | null;
  canSaveSegment: boolean;
  isLoadingSegments: boolean;
  refetchSegments: () => void;
  selectSegment: (segment: Segment) => Promise<void>;
};

export default function useSegments({
  merchantId,
  brandId,
  enabled = true,
  selectedSegmentId,
}: UseSegmentsOptions): UseSegmentsResult {
  const [filtersGroups, searchState] = useCreatorCrmStore(
    useShallow((state) => [
      state.content.filtersState.filtersGroups,
      state.content.filtersState.searchState,
    ])
  );

  const {
    data: segmentsData,
    isLoading: isLoadingSegments,
    refetch: refetchSegments,
  } = useSegmentsQuery({
    merchantId,
    enabled: enabled && !!merchantId,
  });

  const savedSegments = useMemo(
    () => (Array.isArray(segmentsData) ? segmentsData : []),
    [segmentsData]
  );

  const currentFiltersForSegment = useMemo(
    () => convertFiltersToSegment(filtersGroups, searchState ?? undefined),
    [filtersGroups, searchState]
  );

  const activeSegment = useMemo(
    () => savedSegments.find((s) => s.id === selectedSegmentId) ?? null,
    [selectedSegmentId, savedSegments]
  );

  const {campaignSegments, selectCampaignSegment} = useBrandCampaignsSegments({
    merchantId,
    brandId,
    enabled,
  });

  const activeCampaignSegment = useMemo(
    () => campaignSegments.find((s) => s.id === selectedSegmentId) ?? null,
    [selectedSegmentId, campaignSegments]
  );

  const segments = useMemo(
    () => [...savedSegments, ...campaignSegments],
    [savedSegments, campaignSegments]
  );

  const selectSegment = useCallback(
    async (segment: Segment) => {
      if (isCampaignSegment(segment)) {
        await selectCampaignSegment(segment);
        return;
      }
      applySegmentFilters(segment);
    },
    [selectCampaignSegment]
  );

  const canSaveSegment = useMemo(() => {
    const totalActiveFilters =
      searchCreatorsGetters.getTotalActiveFiltersCount(filtersGroups);
    const hasSearchChips = (searchState?.chips?.length ?? 0) > 0;
    return totalActiveFilters > 0 || hasSearchChips;
  }, [filtersGroups, searchState?.chips?.length]);

  return {
    segments,
    currentFiltersForSegment,
    activeSegment,
    activeCampaignSegment,
    canSaveSegment,
    isLoadingSegments,
    refetchSegments,
    selectSegment,
  };
}
